<template>
  <div class="app-whatsapp-panel">
    <p v-if="!chatState" class="text-muted">{{ $t("loading") }}...</p>

    <template v-else-if="!chatState.account_connected">
      <p class="text-muted">{{ $t("whatsapp-not-connected") }}</p>
      <template v-if="chatState.can_link">
        <app-button type="button" :loading="loading" @click="onConnectAccount">
          {{ $t("whatsapp-connect") }}
        </app-button>
        <p v-if="connectLinkOpened" class="text-muted whatsapp-connect-hint">{{ $t("whatsapp-connect-hint") }}</p>
      </template>
    </template>

    <template v-else>
      <!-- Link / change chat controls (VolaTeQ admins only). Changing updates the whole customer_plant. -->
      <div v-if="chatState.can_link" class="app-whatsapp-panel-link-bar">
        <template v-if="!isEditingLink">
          <span v-if="chatState.linked" class="app-whatsapp-panel-chat-name">{{ chatState.chat_name }}</span>
          <span v-else class="text-muted">{{ $t("whatsapp-not-linked") }}</span>
          <app-button type="button" variant="secondary" size="sm" @click="onStartEditLink">
            {{ chatState.linked ? $t("whatsapp-change-chat") : $t("whatsapp-link-chat") }}
          </app-button>
        </template>
        <template v-else>
          <app-multiselect
            v-model="selectedChatId"
            :options="chatOptions"
            :singleSelect="true"
            :searchable="true"
            :allowEmpty="true"
            :loading="chatsLoading"
            :placeholder="$t('whatsapp-select-chat')"
            small
          />
          <app-button type="button" :loading="loading" @click="onSaveLink">{{ $t("save") }}</app-button>
          <app-button type="button" variant="secondary" @click="onCancelEditLink">{{ $t("cancel") }}</app-button>
          <small class="text-muted app-whatsapp-panel-link-hint">{{ $t("whatsapp-link-scope-hint") }}</small>
        </template>
      </div>

      <p v-else-if="!chatState.linked" class="text-muted">{{ $t("whatsapp-not-linked") }}</p>
      <p v-else-if="chatState.chat_name" class="app-whatsapp-panel-chat-name text-muted">{{ chatState.chat_name }}</p>

      <template v-if="chatState.linked">
        <div ref="messagesList" class="app-whatsapp-panel-list mar-bottom">
          <p v-if="chatState.messages.length === 0" class="text-muted">{{ $t("whatsapp-empty") }}</p>

          <div
            v-for="message in chatState.messages"
            :key="message.id"
            class="whatsapp-message-row"
            :class="{
              'whatsapp-message-row--own': !message.is_event && message.is_sender,
              'whatsapp-message-row--event': message.is_event,
            }"
          >
            <div
              class="whatsapp-message"
              :class="{
                'whatsapp-message--own': !message.is_event && message.is_sender,
                'whatsapp-message--event': message.is_event,
              }"
            >
              <template v-if="message.is_event">
                <div class="whatsapp-message-event-body">{{ message.text }}</div>
                <span v-if="message.timestamp" class="whatsapp-message-time text-muted">
                  {{ formatDateTime(message.timestamp) }}
                </span>
              </template>
              <template v-else>
                <div class="whatsapp-message-head">
                  <span class="whatsapp-message-sender-wrap">
                    <span class="whatsapp-sender-avatar" aria-hidden="true">
                      <img
                        v-if="senderPictureUrl(message)"
                        :src="senderPictureUrl(message)"
                        :alt="senderName(message)"
                        class="whatsapp-sender-avatar-image"
                      />
                      <span v-else class="whatsapp-sender-avatar-initials">{{ senderInitials(message) }}</span>
                    </span>
                    <span class="whatsapp-message-sender">{{ senderName(message) }}</span>
                  </span>
                  <span class="whatsapp-message-head-actions">
                    <span class="whatsapp-message-time text-muted">{{ formatDateTime(message.timestamp) }}</span>
                    <button
                      v-if="message.is_sender && !message.deleted"
                      type="button"
                      class="whatsapp-message-delete-btn"
                      :title="$t('delete').toString()"
                      :disabled="deletingMessageId === message.id"
                      @click="onDeleteMessage(message)"
                    >
                      <app-icon icon="trash" />
                    </button>
                  </span>
                </div>

                <div v-if="message.deleted" class="whatsapp-message-deleted text-muted">
                  {{ $t("whatsapp-message-deleted") }}
                </div>
                <template v-else>
                  <div v-if="message.text" class="whatsapp-message-body" v-html="renderText(message)"></div>

                  <div v-if="message.attachments.length" class="whatsapp-message-attachments">
                    <template v-for="attachment in message.attachments">
                      <app-image-lightbox
                        v-if="attachmentKind(attachment) === 'image' && attachmentUrls[attachment.id]"
                        :key="attachment.id"
                        :src="attachmentUrls[attachment.id]"
                        :download-filename="attachment.file_name || ''"
                      >
                        <img
                          :src="attachmentUrls[attachment.id]"
                          :alt="attachment.file_name || ''"
                          class="whatsapp-attachment-image"
                        />
                      </app-image-lightbox>
                      <video
                        v-else-if="attachmentKind(attachment) === 'video' && attachmentUrls[attachment.id]"
                        :key="attachment.id"
                        :src="attachmentUrls[attachment.id]"
                        class="whatsapp-attachment-video"
                        controls
                        preload="metadata"
                      ></video>
                      <audio
                        v-else-if="attachmentKind(attachment) === 'audio' && attachmentUrls[attachment.id]"
                        :key="attachment.id"
                        :src="attachmentUrls[attachment.id]"
                        class="whatsapp-attachment-audio"
                        controls
                        preload="metadata"
                      ></audio>
                      <span
                        v-else-if="isMediaAttachment(attachment)"
                        :key="attachment.id"
                        class="whatsapp-attachment-loading text-muted"
                      >
                        {{ $t("loading") }}...
                      </span>
                      <span v-else :key="attachment.id" class="whatsapp-attachment-file text-muted">
                        <app-icon icon="paperclip" />
                        {{ attachment.file_name || $t("whatsapp-attachment") }}
                      </span>
                    </template>
                  </div>
                </template>
              </template>
            </div>

            <div v-if="!message.is_event && !message.deleted && (message.reactions || []).length" class="whatsapp-message-reactions">
              <span
                v-for="reaction in groupedReactions(message)"
                :key="reaction.emoji"
                class="whatsapp-reaction-chip"
                :class="{ 'is-mine': reaction.reactedByMe }"
                :title="reaction.reactorNames.join(', ')"
              >
                <span class="whatsapp-reaction-emoji">{{ reaction.emoji }}</span>
                <span v-if="reaction.count > 1" class="whatsapp-reaction-count">{{ reaction.count }}</span>
              </span>
            </div>
          </div>
        </div>

        <b-form-group class="app-whatsapp-panel-composer">
          <b-textarea
            v-model="messageText"
            rows="2"
            :placeholder="$t('whatsapp-composer-placeholder')"
            @paste.native="onPasteIntoComposer"
          />

          <div v-if="attachmentFile" class="attached-files-preview">
            <div class="attached-file-chip">
              <app-icon icon="image" />
              <span class="file-name" :title="attachmentFile.name">{{ attachmentFile.name }}</span>
              <button
                type="button"
                class="btn-remove-file"
                @click="onRemoveAttachment"
              >
                <app-icon icon="x" />
              </button>
            </div>
          </div>

          <div class="whatsapp-composer-footer">
            <div class="composer-actions-left">
              <button
                type="button"
                class="btn-attach"
                :title="$t('whatsapp-attach-image').toString()"
                @click="onTriggerFileInput"
              >
                <app-icon icon="plus" />
              </button>
              <input
                ref="fileInput"
                type="file"
                style="display: none"
                accept="image/*"
                @change="onFileSelected"
              />
            </div>
            <app-button type="button" :loading="loading" :disabled="isSendDisabled" @click="onSendMessage">
              {{ $t("send") }}
            </app-button>
          </div>
        </b-form-group>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref } from "vue-property-decorator";
import AppButton from "@/app/shared/components/app-button/app-button.vue";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import AppImageLightbox from "@/app/shared/components/app-image-lightbox/app-image-lightbox.vue";
import AppMultiselect from "@/app/shared/components/app-multiselect/app-multiselect.vue";
import { MultiselectOption } from "@/app/shared/components/app-multiselect/types";
import { BaseAuthOnlineComponent } from "@/app/shared/components/base-auth-component/base-auth-online-component";
import { AnalysisSchema } from "@/app/shared/services/volateq-api/api-schemas/analysis-schema";
import {
  WhatsAppAttachmentSchema,
  WhatsAppChatSchema,
  WhatsAppMessageSchema,
  WhatsAppMessagesSchema,
} from "@/app/shared/services/volateq-api/api-schemas/whatsapp-schema";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import { DomHelper } from "@/app/shared/services/helper/dom-helper";
import { AnalysisNotesHelper } from "@/app/shared/services/helper/analysis-notes-helper";
import {
  WhatsAppAttachmentKind,
  WhatsAppGroupedReaction,
  WhatsAppHelper,
} from "@/app/shared/services/helper/whatsapp-helper";
import { WhatsAppChannel } from "@/app/shared/services/realtime/channels/whatsapp-channel";

@Component({
  name: "app-whatsapp-panel",
  components: {
    AppButton,
    AppIcon,
    AppImageLightbox,
    AppMultiselect,
  },
})
export default class AppWhatsappPanel extends BaseAuthOnlineComponent {
  @Prop({ required: true }) analysis!: AnalysisSchema;
  @Ref() messagesList!: HTMLDivElement;
  @Ref() fileInput!: HTMLInputElement;

  chatState: WhatsAppMessagesSchema | null = null;
  messageText = "";
  /** Single outbound image (paste or file picker). WhatsApp allows one attachment per message. */
  attachmentFile: File | null = null;
  loading = false;
  /** Message id currently being deleted (disables that message's trash button). */
  deletingMessageId: string | null = null;
  connectLinkOpened = false;

  isEditingLink = false;
  availableChats: WhatsAppChatSchema[] = [];
  chatsLoading = false;
  selectedChatId: string | null = null;

  /** Object urls of already downloaded media attachments, by attachment id. */
  attachmentUrls: Record<string, string> = {};
  /** Object urls of sender profile pictures, by Unipile attendee id. */
  senderPictureUrls: Record<string, string> = {};
  /** Attendee ids we already tried to load a picture for (success or failure). */
  private senderPictureLoadAttempted = new Set<string>();

  private realtimeChannel: WhatsAppChannel | null = null;
  private realtimeUnsubscribe: (() => void) | null = null;
  private scrollToBottomCleanup: (() => void) | null = null;

  @CatchError()
  async created() {
    await this.loadChat();
  }

  beforeDestroy() {
    this.scrollToBottomCleanup?.();
    this.scrollToBottomCleanup = null;

    this.realtimeUnsubscribe?.();
    this.realtimeUnsubscribe = null;
    void this.realtimeChannel?.destroy();
    this.realtimeChannel = null;

    for (const objectUrl of Object.values(this.attachmentUrls)) {
      URL.revokeObjectURL(objectUrl);
    }
    this.attachmentUrls = {};

    for (const objectUrl of Object.values(this.senderPictureUrls)) {
      URL.revokeObjectURL(objectUrl);
    }
    this.senderPictureUrls = {};
    this.senderPictureLoadAttempted.clear();
  }

  get isSendDisabled(): boolean {
    return this.messageText.trim() === "" && this.attachmentFile === null;
  }

  /**
   * True while the composer holds an unsent message. Consumers that can close or unmount the
   * panel (the notes thread's tabs, the notes modal) use this to confirm before discarding it.
   */
  get hasUnsavedComposerContent(): boolean {
    return this.messageText.trim() !== "" || this.attachmentFile !== null;
  }

  get chatOptions(): MultiselectOption<string>[] {
    return this.availableChats.map(chat => ({
      id: chat.id,
      label: WhatsAppHelper.formatLinkableChatLabel(chat.type, chat.name, {
        group: this.$t("whatsapp-chat-type-group").toString(),
        direct: this.$t("whatsapp-chat-type-direct").toString(),
        unnamed: this.$t("whatsapp-unnamed-chat").toString(),
      }),
    }));
  }

  senderName(message: WhatsAppMessageSchema): string {
    if (message.is_sender) {
      return this.$t("whatsapp-own-sender").toString();
    }
    return message.sender_name || this.$t("whatsapp-unknown-sender").toString();
  }

  senderInitials(message: WhatsAppMessageSchema): string {
    return WhatsAppHelper.senderInitials(this.senderName(message));
  }

  senderPictureUrl(message: WhatsAppMessageSchema): string | null {
    if (!message.sender_attendee_id) {
      return null;
    }
    return this.senderPictureUrls[message.sender_attendee_id] || null;
  }

  renderText(message: WhatsAppMessageSchema): string {
    return WhatsAppHelper.renderMessageHtml(message.text || "");
  }

  groupedReactions(message: WhatsAppMessageSchema): WhatsAppGroupedReaction[] {
    return WhatsAppHelper.groupReactions(
      message.reactions || [],
      this.$t("whatsapp-own-sender").toString(),
      this.$t("whatsapp-unknown-sender").toString(),
    );
  }

  formatDateTime(isoDate: string | null): string {
    return WhatsAppHelper.formatDateTime(isoDate);
  }

  attachmentKind(attachment: WhatsAppAttachmentSchema): WhatsAppAttachmentKind {
    return WhatsAppHelper.attachmentKind(attachment);
  }

  isMediaAttachment(attachment: WhatsAppAttachmentSchema): boolean {
    return WhatsAppHelper.isMediaAttachment(attachment);
  }

  @CatchError("loading")
  async onConnectAccount() {
    const { url } = await this.volateqApi.getWhatsAppConnectLink();
    window.open(url, "_blank", "noopener");
    this.connectLinkOpened = true;
  }

  @CatchError("chatsLoading")
  async onStartEditLink() {
    this.availableChats = await this.volateqApi.getWhatsAppChats();
    this.selectedChatId = null;
    this.isEditingLink = true;
  }

  onCancelEditLink() {
    this.isEditingLink = false;
    this.selectedChatId = null;
  }

  @CatchError("loading")
  async onSaveLink() {
    const selectedChat = this.availableChats.find(chat => chat.id === this.selectedChatId);
    await this.volateqApi.linkAnalysisWhatsAppChat(
      this.analysis.id,
      this.selectedChatId,
      selectedChat?.name || null,
    );

    this.isEditingLink = false;
    this.selectedChatId = null;
    await this.loadChat();
  }

  @CatchError("loading")
  async onSendMessage() {
    const messageText = this.messageText.trim();
    const attachmentFile = this.attachmentFile;
    if (!messageText && !attachmentFile) {
      return;
    }

    // WhatsApp messages leave the WebApp for a real client group chat, so require an
    // explicit confirmation and never send on Ctrl+Enter.
    if (!window.confirm(this.$t("whatsapp-send-confirm").toString())) {
      return;
    }

    await this.volateqApi.sendAnalysisWhatsAppMessage(this.analysis.id, messageText, attachmentFile);
    this.messageText = "";
    this.attachmentFile = null;
    // The user just sent a message: show it, wherever they were scrolled to.
    await this.updateMessages(true);
  }

  @CatchError()
  async onDeleteMessage(message: WhatsAppMessageSchema) {
    if (!message.is_sender || message.is_event || message.deleted) {
      return;
    }

    // Delete-for-everyone reaches every participant of the client chat — confirm first.
    if (!window.confirm(this.$t("whatsapp-delete-confirm").toString())) {
      return;
    }

    this.deletingMessageId = message.id;
    try {
      await this.volateqApi.deleteAnalysisWhatsAppMessage(this.analysis.id, message.id);
      await this.updateMessages();
    } finally {
      this.deletingMessageId = null;
    }
  }

  @CatchError()
  onPasteIntoComposer(event: ClipboardEvent) {
    const images = AnalysisNotesHelper.extractImageFilesFromClipboard(event);
    if (images.length === 0) {
      return;
    }

    // Pasted a screenshot/image: attach it (single image only) instead of inserting raw clipboard content.
    event.preventDefault();
    this.attachmentFile = images[0];
  }

  @CatchError()
  onTriggerFileInput() {
    this.fileInput?.click();
  }

  @CatchError()
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];
    if (selectedFile && selectedFile.type.startsWith("image/")) {
      this.attachmentFile = selectedFile;
    }
    input.value = "";
  }

  @CatchError()
  onRemoveAttachment() {
    this.attachmentFile = null;
  }

  private async loadChat(): Promise<void> {
    this.chatState = await this.volateqApi.getAnalysisWhatsAppMessages(this.analysis.id);

    // Right after the hosted auth QR flow the connected account is not synced yet;
    // syncing via the account status endpoint persists it, then the messages are retried.
    if (!this.chatState.account_connected && this.chatState.can_link) {
      const accountStatus = await this.volateqApi.getWhatsAppAccountStatus();
      if (accountStatus.connected) {
        this.chatState = await this.volateqApi.getAnalysisWhatsAppMessages(this.analysis.id);
      }
    }

    if (this.chatState.linked) {
      await this.loadMediaAttachments(this.chatState.messages);
      await this.loadSenderPictures(this.chatState.messages);
      this.scrollMessagesListToBottom();
      this.joinRealtimeChannel();
    }
  }

  private async updateMessages(forceScrollToBottom = false): Promise<void> {
    const chatState = await this.volateqApi.getAnalysisWhatsAppMessages(this.analysis.id);
    if (!chatState.linked) {
      return;
    }

    // An incoming message must not pull a user who scrolled up back down to the newest one.
    const wasScrolledToBottom = !this.messagesList || DomHelper.isScrolledToBottom(this.messagesList);

    this.chatState = chatState;
    await this.loadMediaAttachments(chatState.messages);
    await this.loadSenderPictures(chatState.messages);

    if (forceScrollToBottom || wasScrolledToBottom) {
      this.scrollMessagesListToBottom();
    }
  }

  private async loadMediaAttachments(messages: WhatsAppMessageSchema[]): Promise<void> {
    const pendingAttachments = WhatsAppHelper.collectMediaAttachments(messages)
      .filter(mediaRef => !(mediaRef.attachment.id in this.attachmentUrls));

    await Promise.all(pendingAttachments.map(async mediaRef => {
      try {
        const attachmentBlob = await this.volateqApi.getAnalysisWhatsAppAttachment(
          this.analysis.id,
          mediaRef.messageId,
          mediaRef.attachment.id,
        );
        this.$set(this.attachmentUrls, mediaRef.attachment.id, URL.createObjectURL(attachmentBlob));
      } catch (e) {
        // A failed media download must not break the whole conversation view.
        console.error("Failed to load WhatsApp attachment:", e);
      }
    }));
  }

  private async loadSenderPictures(messages: WhatsAppMessageSchema[]): Promise<void> {
    const pendingAttendeeIds = WhatsAppHelper.collectSenderAttendeeIds(messages)
      .filter(attendeeId => !this.senderPictureLoadAttempted.has(attendeeId));

    await Promise.all(pendingAttendeeIds.map(async attendeeId => {
      this.senderPictureLoadAttempted.add(attendeeId);
      try {
        const pictureBlob = await this.volateqApi.getAnalysisWhatsAppAttendeePicture(
          this.analysis.id,
          attendeeId,
        );
        if (!pictureBlob.type.startsWith("image/")) {
          return;
        }
        this.$set(this.senderPictureUrls, attendeeId, URL.createObjectURL(pictureBlob));
      } catch (e) {
        // Missing or failed profile pictures fall back to initials.
        console.error("Failed to load WhatsApp sender picture:", e);
      }
    }));
  }

  private joinRealtimeChannel(): void {
    const channelId = this.chatState?.channel_id;
    if (!channelId) {
      return;
    }

    if (!this.realtimeChannel) {
      this.realtimeChannel = new WhatsAppChannel();
      this.realtimeUnsubscribe = this.realtimeChannel.subscribe(() => {
        void this.updateMessages().catch(e => {
          console.error("Failed to update WhatsApp messages:", e);
        });
      });
    }

    void this.realtimeChannel.join(channelId);
  }

  private scrollMessagesListToBottom(): void {
    this.scrollToBottomCleanup?.();
    this.scrollToBottomCleanup = null;

    void this.$nextTick(() => {
      if (this.messagesList) {
        this.scrollToBottomCleanup = DomHelper.scrollToBottomWhenContentSettled(this.messagesList);
      }
    });
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-whatsapp-panel {
  .app-whatsapp-panel-link-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    margin-bottom: 0.5rem;

    .app-multiselect {
      flex: 1;
      min-width: 180px;
    }
  }

  .app-whatsapp-panel-link-hint {
    flex-basis: 100%;
  }

  .app-whatsapp-panel-chat-name {
    font-weight: 600;
  }

  .app-whatsapp-panel-list {
    max-height: 600px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .whatsapp-message-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: flex-start;
    max-width: 85%;

    &.whatsapp-message-row--own {
      align-self: flex-end;
      align-items: flex-end;
    }

    &.whatsapp-message-row--event {
      align-self: center;
      align-items: center;
      max-width: 95%;
    }
  }

  .whatsapp-message {
    max-width: 100%;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    background-color: rgba(0, 0, 0, 0.05);

    &.whatsapp-message--own {
      background-color: rgba(37, 211, 102, 0.15); // WhatsApp-green tint for own messages
    }

    &.whatsapp-message--event {
      padding: 0.25rem 0.75rem;
      background-color: transparent;
      text-align: center;
    }
  }

  .whatsapp-message-head {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
  }

  .whatsapp-message-head-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    flex-shrink: 0;
  }

  .whatsapp-message-delete-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    color: var(--muted-text-color, #6c757d);
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.15s, color 0.15s;

    &:hover:not(:disabled) {
      opacity: 1;
      color: var(--danger-text-color);
    }

    &:disabled {
      cursor: default;
      opacity: 0.4;
    }

    &:focus {
      outline: none;
    }
  }

  .whatsapp-message-sender-wrap {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .whatsapp-sender-avatar {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.12);
    color: var(--default-text-color);
    font-size: 0.6rem;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    user-select: none;
  }

  .whatsapp-message--own .whatsapp-sender-avatar {
    background-color: rgba(37, 211, 102, 0.35);
  }

  .whatsapp-sender-avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .whatsapp-sender-avatar-initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .whatsapp-message-sender {
    font-weight: 600;
    font-size: 0.85rem;
  }

  .whatsapp-message-time {
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .whatsapp-message-body {
    word-break: break-word;
    white-space: normal;
  }

  .whatsapp-message-deleted {
    font-style: italic;
    word-break: break-word;
  }

  .whatsapp-message-event-body {
    font-size: 0.85rem;
    color: var(--muted-text-color, #6c757d);
    word-break: break-word;
  }

  .whatsapp-message-reactions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: -0.4rem;
    margin-left: 0.4rem;
    margin-right: 0.4rem;
    position: relative;
    z-index: 1;
  }

  .whatsapp-message-row--own .whatsapp-message-reactions {
    margin-left: 0;
  }

  .whatsapp-reaction-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.2em;
    padding: 0.15em 0.5em;
    background-color: var(--default-bg-color);
    border: 1px solid var(--default-border-color);
    border-radius: 0.75rem;
    box-shadow: 0 1px 2px var(--default-shadow-color);
    color: var(--default-text-color);
    font-size: 0.85rem;
    line-height: 1.4;

    &.is-mine {
      border-color: rgba(37, 211, 102, 0.65);
      background-color: var(--secondary-bg-highlight-color);
    }
  }

  .whatsapp-reaction-count {
    font-weight: 600;
    font-size: 0.75rem;
    color: var(--muted-text-color);
  }

  .whatsapp-message-attachments {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.25rem;
  }

  .whatsapp-attachment-image {
    max-width: 220px;
    max-height: 220px;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .whatsapp-attachment-video {
    max-width: 100%;
    max-height: 260px;
    border-radius: 0.25rem;
  }

  .whatsapp-attachment-audio {
    width: 100%;
    max-width: 320px;
  }

  .whatsapp-attachment-file {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .whatsapp-connect-hint {
    margin-top: 0.5rem;
  }

  .whatsapp-composer-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .composer-actions-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-attach {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: var(--default-bg-hover-color);
    border: 1px solid var(--default-border-color);
    color: var(--default-text-color);
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: var(--secondary-bg-highlight-color);
      border-color: var(--primary-border-color);
      color: var(--primary-bg-color);
    }

    &:focus {
      outline: none;
    }
  }

  .attached-files-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4em;
    margin-top: 0.5em;
    padding: 0.4em;
    background-color: var(--default-bg-hover-color);
    border: 1px solid var(--default-border-color);
  }

  .attached-file-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    background-color: var(--default-bg-color);
    border: 1px solid var(--default-border-color);
    padding: 0.2em 0.5em;
    font-size: 0.75rem;
    color: var(--default-text-color);

    .file-name {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .btn-remove-file {
      background: none;
      border: none;
      padding: 0;
      margin-left: 0.2em;
      color: var(--danger-text-color);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
      transition: opacity 0.2s;

      &:hover {
        opacity: 1;
      }

      &:focus {
        outline: none;
      }
    }
  }
}
</style>
